import { Form, FormInstance, Input, Select, Slider } from "antd"
import {
  IndentedSection,
  SettingRow,
} from "../setup/components/CreateShiftPlanConfiguration"
import { ReactNode } from "react"
import { BaseOptionType, DefaultOptionType } from "antd/lib/select"
import { RowDivider } from "./NamePermissionClockIn"

type SettingModificationProps = {
  HARDWARE_DEVICE_OPTIONS: (DefaultOptionType | BaseOptionType)[]
  sufficeIcon: ReactNode
  BUFFER_WINDOW_OPTIONS: (DefaultOptionType | BaseOptionType)[]
  NOTIFICATION_CADENCE_OPTIONS: (DefaultOptionType | BaseOptionType)[]
  form: FormInstance
}
const SettingModification: React.FC<SettingModificationProps> = ({
  HARDWARE_DEVICE_OPTIONS,
  sufficeIcon,
  BUFFER_WINDOW_OPTIONS,
  NOTIFICATION_CADENCE_OPTIONS,
  form,
}) => {
  const allowManualEntries = Form.useWatch("allowManualTimeEntry", form)
  const smsNotification = Form.useWatch("smsNotification", form)
  const allowSwapping = Form.useWatch("allowSwapping", form)
  const useBufferWindow = Form.useWatch("allowLateClockIn", form)
  const useOvertimeRecord = Form.useWatch("useOvertimeRecord", form)
  const linkHardwareDevice = Form.useWatch("linkHardwareDevice", form)
  const geofenceLock = Form.useWatch("allowGeoFencing", form)
  const geofenceOfferMeters = Form.useWatch("geofencingOffsetMeters", form)
  const geofenceOfferMetersNumber = (() => {
    const n =
      typeof geofenceOfferMeters === "number"
        ? geofenceOfferMeters
        : Number(geofenceOfferMeters)
    return Number.isFinite(n) ? n : 0
  })()

  return (
    <div className="flex flex-col gap-5 px-4 py-4">
      {/* Manual entries */}
      <div className="flex flex-col gap-2">
        <SettingRow
          name="allowManualTimeEntry"
          label="Allow manual time entries?"
          description="Let employees add clock-in/out times manually if they forget or are unable to use the standard methods."
        />
        {allowManualEntries && (
          <IndentedSection>
            <SettingRow
              name="requireApprovalForManualTimeEntry"
              label="Require approval for manual entries?"
              description="Require an admin to approve manual entries before they get added to payroll."
            />
          </IndentedSection>
        )}
      </div>

      <RowDivider />

      <SettingRow
        name="useInPayrollCalculation"
        label="Use time & attendance data for payroll"
        description="Allow time and attendance data to feed into the payroll calculations."
        confirmOnDisable={{
          title: "Disable time & attendance for payroll?",
          message:
            "Time and attendance data will not feed into payroll calculations for this shift plan.",
        }}
      />

      <RowDivider />

      {/* Shift swapping */}
      <div className="flex flex-col gap-2">
        <SettingRow
          name="allowSwapping"
          label="Allow shift swapping"
          description="Allow employees to swap shifts."
        />
        {allowSwapping && (
          <IndentedSection>
            <SettingRow
              name="requireApprovalForSwap"
              label="Require approval for shift swap actions"
              description="Require an admin to approve/deny all shift swap actions employees try to undertake."
            />
          </IndentedSection>
        )}
      </div>

      <RowDivider />
      <SettingRow
        name="smsNotification"
        label="Enable SMS notifications for roster changes"
        description="Send SMS alerts to staff when they're assigned to, removed from, or have changes made to their shifts. Messages are batched and sent based on the notification cadence you set below."
      />
      {smsNotification && (
        <IndentedSection>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-[#64748B] bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]">
              SMS notifications are a paid add-on. You'll only be charged for
              messages actually sent — usage is tracked across the month and
              billed at the end of your billing cycle.
            </p>

            <div>
              <p className="text-sm font-semibold text-[#0F172B] mb-1">
                Notification cadence
              </p>
              <Form.Item
                name="notificationCadence"
                className="mb-0"
              >
                <Select
                  allowClear
                  placeholder="Select how often SMS notifications are batched and sent out e.g 15 mins, 30 mins, 1 hr, 2hrs"
                  suffixIcon={sufficeIcon}
                  options={NOTIFICATION_CADENCE_OPTIONS}
                  className="[&_.ant-select-selector]:!min-h-[40px] [&_.ant-select-selector]:!rounded-lg"
                />
              </Form.Item>
            </div>

            <SettingRow
              name="notifiyMeBefore"
              label="Notify me before SMS sends"
              description="Get a heads-up when your edits are about to trigger an SMS to staff. Leave this off and messages will send automatically at each cadence interval — no confirmations needed."
            />
          </div>
        </IndentedSection>
      )}
      <RowDivider />

      {/* Buffer window */}
      <div className="flex flex-col gap-2">
        <SettingRow
          name="allowLateClockIn"
          label="Use grace period"
          description="Don't flag as late clock-in if within grace period."
        />
        {useBufferWindow && (
          <IndentedSection>
            <p className="text-sm font-semibold text-[#0F172B] mb-1">
              Grace period
            </p>
            <Form.Item
              name="allowedLateClockinInMinutes"
              className="mb-0"
            >
              <Select
                placeholder="Select buffer window e.g 15 mins, 30 mins, 1 hr"
                options={BUFFER_WINDOW_OPTIONS}
                suffixIcon={sufficeIcon}
                className="[&_.ant-select-selector]:!min-h-[40px] [&_.ant-select-selector]:!rounded-lg"
              />
            </Form.Item>
          </IndentedSection>
        )}
      </div>

      <RowDivider />

      <SettingRow
        name="useOvertimeRecord"
        label="Collect overtime record"
        description="Collect overtime records from employees"
      />
      {useOvertimeRecord && (
        <IndentedSection>
          {/* Overtime */}
          <SettingRow
            name="allowOvertime"
            label="Add overtime to payroll"
            description="Allow employees overtime to be considered for payroll."
          />
        </IndentedSection>
      )}

      <RowDivider />

      {/* Hardware devices */}
      <div className="flex flex-col gap-2">
        <SettingRow
          name="linkHardwareDevice"
          label="Use hardware clock-in devices?"
          description="Do you want to use a hardware clock-in device? We'll help you set it up."
        />
        {linkHardwareDevice && (
          <IndentedSection>
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[#64748B] bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]">
                Setting up hardware devices requires assistance from our
                technical team. We'll reach out to your admin email to get
                started, or you can provide an alternate contact email below.
              </p>

              <div>
                <p className="text-sm font-semibold text-[#0F172B] mb-1">
                  Hardware device type
                </p>
                <Form.Item
                  name="hardwareDeviceTypes"
                  className="mb-0"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select your hardware type"
                    suffixIcon={sufficeIcon}
                    options={HARDWARE_DEVICE_OPTIONS}
                    className="[&_.ant-select-selector]:!min-h-[40px] [&_.ant-select-selector]:!rounded-lg"
                  />
                </Form.Item>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#0F172B] mb-1">
                  Additional contact email
                </p>
                <Form.Item
                  name="hardwareSetupContactEmail"
                  className="mb-0"
                >
                  <Input
                    placeholder="Enter your email address"
                    className="h-10 rounded-lg"
                  />
                </Form.Item>
              </div>
            </div>
          </IndentedSection>
        )}
      </div>

      <RowDivider />

      {/* Geofence */}
      <div className="flex flex-col gap-2">
        <SettingRow
          name="allowGeoFencing"
          label="Geofence lock"
          description="Require employees to be within a specific radius of the workplace location to clock in or out."
        />
        {geofenceLock && (
          <IndentedSection>
            <p className="text-sm font-semibold text-[#0F172B] mb-1">
              Geofence radius
            </p>
            <Form.Item
              name="geofencingOffsetMeters"
              className="mb-0"
            >
              <Slider
                min={0}
                max={500}
                step={1}
                tooltip={{
                  formatter: (val) => `${val} M`,
                  open: true,
                }}
                className="[&_.ant-slider-track]:bg-[#004AF5] [&_.ant-slider-rail]:bg-gray-200 [&_.ant-slider-handle]:border-[#004AF5] [&_.ant-slider-handle]:bg-white"
              />
            </Form.Item>
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#64748B]">
                {/* {geofenceOfferMetersNumber.toFixed(0)} M */}0 M
              </p>
              <p className="text-sm text-[#64748B]">500 M</p>
            </div>
          </IndentedSection>
        )}
      </div>
    </div>
  )
}

export default SettingModification
